<template>
  <div class="user-from-excel-uploader">
    <template v-if="is_error || formErrors.length">
      <div v-for="(error, index) in formErrors" :key="'excel-error-' + index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: red">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>

    <div class="card mt-4">
      <div class="uk-card-header p-0">
        <div class="float-right"></div>
      </div>

      <div class="uk-card-body p-0 uk-grid-collapse uk-grid-large" uk-grid>
        <div class="uk-padding-large position-relative uk-width-1-4@m">
          <h5><i class="uil-table"></i> {{ $t('general.add_with_excell') }}:</h5>
          <div class="file-upload-container mb-3">
            <div :class="{
              'file-upload-disabled': is_loading,
              'file-upload-has-file': fileInfo.file,
            }" class="file-upload-area" @click="triggerExcelInput">
              <input id="fileToUpload" ref="fileInput" :disabled="is_loading" accept=".xls, .xlsx"
                class="file-upload-input" name="fileToUpload" type="file" @click="onExcelInputClick" @change="setFile" />
              <div class="file-upload-content">
                <i class="uil-cloud-upload file-upload-icon"></i>
                <p class="file-upload-text">
                  {{ fileInfo.file ? fileInfo.name : $t('general.select_excel_file') }}
                </p>
                <span v-if="fileInfo.file" class="file-info">
                  {{ $t('general.person_count_short', { count: fileInfo.count }) }}
                </span>
              </div>
            </div>

            <button class="btn btn-info template-download-btn" type="button" @click="exportSampleFile">
              <i class="uil-download-alt"></i> {{ $t('general.download_excel_template') }}
              <i class="uil-info-circle ml-1" style="font-size: 1rem"
                :uk-tooltip="'title: ' + $t('general.user_excel_template_tooltip') + '; pos: bottom-center'"></i>
            </button>
          </div>

          <div v-if="$isSuperAdmin">
            <hr class="mt-4" />
            <h5 class="section-subtitle"><i class="uil-wifi"></i> {{ $t("general.transfer_user_to_institution") }} </h5>
            <div class="form-group row mb-3">
              <div class="col-md-12">
                <label class="col-form-label" for="company">
                  {{ $t("users.institution") }} {{ $t("general.select_verb") }}
                  <span class="required">*</span>
                </label>
                <multiple-select-input :model.sync="company" :required="true" :searchable="true" :showLabels="false"
                  :values="formattedInstitution" />
              </div>
            </div>
          </div>

          <hr class="mt-4" />
          <h5 class="section-subtitle"><i class="uil-users-alt"></i> {{ $t("general.transfer_user_to_group") }}</h5>
          <div class="form-group row mb-3">
            <div class="col-md-12">
              <label class="col-form-label" for="company">{{
                $t("general.choose_group")
              }}</label>
              <multiple-select-input :model.sync="selectedUserGroup" :searchable="formattedUserGroup.length > 10"
                :showLabels="false" :values="formattedUserGroup" />
            </div>
          </div>
        </div>

        <div class="uk-padding-large uk-width-expand d-flex flex-column justify-content-between px-2"
          style="border-left: 1px solid #ccc">
          <default-loading v-if="user_loading || is_loading"></default-loading>
          <template v-else>
            <div class="text-right">
              <button v-if="!is_loading && importedUsers.length" class="uk-button uk-button-primary small"
                style="margin-top: -15px" type="button" @click="submitFile">
                <i class="icon-feather-save"></i> {{ $t("general.save") }}
              </button>
            </div>

            <div class="uk-position-relative my-4 text-center" style="overflow-y: scroll; padding-right: 2rem">
              <h4 v-if="is_added" class="text-left">
                {{ $t('general.users_listed_in_file', { name: fileInfo.name, count: fileInfo.count }) }}
              </h4>

              <div class="table-responsive">
                <table class="table align-items-center imported-users-table">
                  <thead>
                    <tr>
                      <th class="col-index" scope="col">#</th>
                      <th class="col-action" scope="col"></th>
                      <th v-for="column in visibleTableHeaders" :key="'header-' + column.index" scope="col">
                        <span :title="column.header" class="header-clamp">{{ column.header }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="list">
                    <tr v-for="(item, index) in importedUsers" :key="'row-' + index">
                      <td class="col-index">{{ index + 1 }}</td>
                      <td class="col-action text-center">
                        <div class="actions">
                          <a class="btn btn-icon btn-hover btn-lg btn-circle" href="#" :uk-tooltip="$t('general.remove_from_list')"
                            @click.prevent="removeIndex(index)">
                            <i class="uil-times-circle text-danger"></i>
                          </a>
                        </div>
                      </td>
                      <td v-for="column in visibleTableHeaders" :key="'cell-' + index + '-' + column.index"
                        :title="item[column.index]">
                        {{ item[column.index] }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="text-right">
              <button v-if="!is_loading && importedUsers.length" class="uk-button uk-button-primary small"
                style="margin-bottom: -15px" type="button" @click="submitFile">
                <i class="icon-feather-save"></i> {{ $t("general.save") }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <div class="uk-card-footer p-0">
        <div class="float-right"></div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/core/services";
import { downloadFromUrl, exportFileName, readBlobError } from "@/utils/fileDownload";

/**
 * Native secicide MIME ile DARALTMA YAPILMIYOR.
 *
 * Neden: Android'de .xlsx dosyalari cogu zaman `application/octet-stream`
 * olarak raporlanir. Resmi Excel MIME'i verilince dosyalar listede gorunur
 * ama SECILEMEZ. Uzanti (".xlsx") ise Expo DocumentPicker'da hic karsilik
 * bulmaz. Bu yuzden secim serbest birakilip dogrulama JS'te yapiliyor —
 * dosya adi elimizde, guvenilir kontrol orada.
 */
const EXCEL_PICK_MIME = "*/*";
const EXCEL_EXTENSIONS = [".xls", ".xlsx"];

function isExcelFileName(name) {
  const value = String(name || "").toLowerCase();
  return EXCEL_EXTENSIONS.some((ext) => value.endsWith(ext));
}
import readXlsxFile from "read-excel-file";
import * as XLSX from "xlsx";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import company_module, {
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEMS as GET_INSTITUTIONS,
  ITEMS as INSTITUTIONS,
  MODULE_NAME as COMPANY_MODULE,
} from "@/core/services/store/company.module";
import user_module, {
  IMPORT_BASE_URL as USER_IMPORT_URL,
  IMPORT_USERS,
  LOADING as USER_LOADING,
  MODULE_NAME as USER_MODULE,
  ERROR as USER_ERRORS,
} from "@/core/services/store/user.module";
import user_group_module, {
  BASE_URL as USER_GROUP_BASE_URL,
  GET_ITEMS as GET_USER_GROUPS,
  ITEMS as USER_GROUPS,
  MODULE_NAME as USER_GROUP_MODULE,
} from "@/core/services/store/user_group.module";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";

const _USER_GROUP_MODULE_NAME = USER_GROUP_MODULE;
const _COMPANY_MODULE_NAME = COMPANY_MODULE;
const _USER_MODULE = USER_MODULE;

export default {
  name: "UserFromExcelUploader",
  components: {
    DefaultLoading,
    MultipleSelectInput,
  },
  props: {
    sampleExcelEndpoint: {
      type: String,
      default: "/api/user/export-template",
    },
    sampleFileName: {
      type: String,
      default: "user_sample",
    },
    excelHasHeader: {
      type: Boolean,
      default: true,
    },
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_COMPANY_MODULE_NAME, company_module);
    registerStoreModule(_USER_GROUP_MODULE_NAME, user_group_module);
    registerStoreModule(_USER_MODULE, user_module);
  },
  data() {
    return {
      file: null,
      file_value: null,
      importedUsers: [],
      originalImportedUsers: [],
      tableHeaders: [],
      removedItems: [],
      user_group: null,
      selectedUserGroup: {
        id: null,
        name: null,
      },
      company_id: null,
      company: {
        id: null,
        name: null,
      },
      fileInfo: {
        name: "",
        count: 0,
        file: null,
      },
      is_added: false,
      is_loading: false,
      is_error: false,
      formErrors: [],
      _waitingForFile: false,
    };
  },
  computed: {
    userInfo: {
      get() {
        return JSON.parse(this.secureLocalStorage("user"));
      },
      set() {
      },
    },
    user_loading: {
      get() {
        return store.getters[USER_MODULE + "/" + USER_LOADING];
      },
      set() {
      },
    },
    errors: {
      get() {
        return store.getters[USER_MODULE + "/" + USER_ERRORS];
      },
      set() {
      },
    },
    institutions: {
      get() {
        return store.getters[COMPANY_MODULE + "/" + INSTITUTIONS];
      },
      set() {
      },
    },
    user_groups: {
      get() {
        return store.getters[USER_GROUP_MODULE + "/" + USER_GROUPS];
      },
      set() {
      },
    },
    formattedInstitution() {
      const results = [
        {
          value: null,
          name: this.$t("general.choose"),
        },
      ];

      if (this.institutions === null || !this.institutions.length) return [];

      this.institutions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });

      return results;
    },
    formattedUserGroup() {
      const groups = [];

      if (this.user_groups === null || !this.user_groups.length) return [];

      this.user_groups.forEach((item) => {
        if (item.status === 1 || item.status) {
          groups.push({
            id: item.id,
            name: item.name,
          });
        }
      });

      return groups;
    },
    effectiveCompanyId() {
      return this.$isSuperAdmin ? this.company_id : this.userInfo?.company_id;
    },
    shouldHideEmailField() {
      return Number(this.effectiveCompanyId) === 489;
    },
    emailColumnIndex() {
      return this.getEmailColumnIndex(this.tableHeaders);
    },
    visibleTableHeaders() {
      return this.tableHeaders
        .map((header, index) => ({ header, index }))
        .filter(({ index }) => !this.shouldHideEmailField || index !== this.emailColumnIndex);
    },
  },
  watch: {
    company() {
      this.company_id = this.company.id;
    },
    selectedUserGroup() {
      this.user_group = this.selectedUserGroup.id;
    },
    errors(val) {
      if (val) {
        this.is_loading = false;
        this.formErrors.push(val);
        this.$nextTick(() => {
          this.scrollToTop();
        });
      }
    },
  },
  mounted() {
    this.getInstitutions();
    this.getUserGroups();

    if (bridge.isNative) {
      this._unsubscribePickFile = bridge.on("PICK_FILE_RESULT", (payload) => {
        if (!this._waitingForFile) return;
        this._waitingForFile = false;

        if (!payload.success || !payload.file) return;
        const { name, type, base64 } = payload.file;

        if (!isExcelFileName(name)) {
          this.formErrors.push(this.$t("general.invalid_file_type"));
          return;
        }

        const byteString = atob(base64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        this.applyExcelFile(new File([ab], name, { type }), name);
      });
    }
  },
  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },
  methods: {
    getInstitutions() {
      this.$store.dispatch(COMPANY_MODULE + "/" + GET_INSTITUTIONS, {
        url: COMPANY_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getUserGroups() {
      this.$store.dispatch(USER_GROUP_MODULE + "/" + GET_USER_GROUPS, {
        url: USER_GROUP_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    resetFileInfo() {
      this.is_added = false;
      this.file = null;
      this.file_value = null;
      this.fileInfo.file = null;
      this.importedUsers = [];
      this.originalImportedUsers = [];
      this.tableHeaders = [];
      this.removedItems = [];
      this.formErrors = [];
      this.is_error = false;

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    onExcelInputClick(event) {
      if (!bridge.isNative) {
        return;
      }
      event.preventDefault();
      if (this.is_loading) {
        return;
      }
      this._waitingForFile = true;
      native.pickFile(EXCEL_PICK_MIME);
    },
    triggerExcelInput(event) {
      if (this.is_loading) {
        return;
      }
      if (event && event.target === this.$refs.fileInput) {
        if (bridge.isNative) {
          event.preventDefault();
          this._waitingForFile = true;
          native.pickFile(EXCEL_PICK_MIME);
        }
        return;
      }
      if (bridge.isNative) {
        event.preventDefault();
        this._waitingForFile = true;
        native.pickFile(EXCEL_PICK_MIME);
        return;
      }
      const input = this.$refs.fileInput;
      if (!input) {
        return;
      }
      input.value = null;
      input.click();
    },
    applyExcelFile(file, fileValue) {
      this.is_added = false;
      this.importedUsers = [];
      this.originalImportedUsers = [];
      this.tableHeaders = [];
      this.removedItems = [];
      this.formErrors = [];
      this.is_error = false;

      this.file_value = fileValue || (file && file.name) || "";
      this.file = file;

      if (this.file) {
        this.excelToJSON();
      }
    },
    setFile(e) {
      this.applyExcelFile(e.target.files[0], e.target.value);
    },
    excelToJSON() {
      if (!this.file) {
        return;
      }

      this.is_loading = true;
      const fileName = this.file_value
        ? this.file_value.split(/(\\|\/)/g).pop()
        : "";

      this.fileInfo.file = this.file;
      readXlsxFile(this.file)
        .then((rows) => {
          if (!rows || rows.length < 2) {
            this.is_loading = false;
            this.formErrors.push(this.$t("general.error_occurred"));
            this.scrollToTop();
            return;
          }

          let dataRows = rows;

          if (this.excelHasHeader) {
            this.tableHeaders = rows[0];
            dataRows = rows.slice(1).filter((row) => row && row.length > 0);
          } else {
            this.tableHeaders = rows[0].map((_, index) => String(index + 1));
          }

          this.updateImporteValues(dataRows, dataRows.length, fileName);
        })
        .catch(() => {
          this.is_loading = false;
          this.formErrors.push(this.$t("general.error_occurred"));
          this.scrollToTop();
        });
    },
    removeIndex(displayIndex) {
      this.removedItems.push(displayIndex + 1);
      this.importedUsers.splice(displayIndex, 1);
      this.fileInfo.count = this.importedUsers.length;
      this.is_added = this.importedUsers.length > 0;
    },
    updateImporteValues(values, count, fileName) {
      this.importedUsers = this.cloneRows(values);
      this.originalImportedUsers = this.cloneRows(values);
      this.fileInfo.name = fileName;
      this.fileInfo.count = count;
      this.is_added = count > 0;
      this.is_loading = false;
    },
    cloneRows(rows) {
      return rows.map((row) => (Array.isArray(row) ? [...row] : []));
    },
    normalizeExcelHeader(header) {
      return String(header || "")
        .toLocaleLowerCase("tr-TR")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, "");
    },
    isEmailHeader(header) {
      const normalizedHeader = this.normalizeExcelHeader(header);
      return ["email", "eposta", "epostadresi", "emailadresi", "mail"].includes(normalizedHeader);
    },
    getEmailColumnIndex(headers) {
      return headers.findIndex((header) => this.isEmailHeader(header));
    },
    generateMadParfumEmail() {
      const randomKey = `${Date.now().toString(36)}${Math.random()
        .toString(36)
        .slice(2, 10)}`;

      return `${randomKey}@madparfum.com`;
    },
    ensureRowsHaveEmail(rows, emailColumnIndex) {
      return this.cloneRows(rows).map((row) => {
        while (row.length <= emailColumnIndex) {
          row.push(null);
        }

        if (!row[emailColumnIndex] || String(row[emailColumnIndex]).trim().length === 0) {
          row[emailColumnIndex] = this.generateMadParfumEmail();
        }

        return row;
      });
    },
    getXlsxFileName(fileName) {
      return `${String(fileName || "users").replace(/\.(xls|xlsx)$/i, "")}.xlsx`;
    },
    getUploadFile() {
      if (!this.shouldHideEmailField) {
        return this.fileInfo.file;
      }

      const headers = [...this.tableHeaders];
      let emailColumnIndex = this.emailColumnIndex;

      if (emailColumnIndex === -1) {
        headers.push("*E-Mail");
        emailColumnIndex = headers.length - 1;
      }

      const sourceRows = this.originalImportedUsers.length
        ? this.originalImportedUsers
        : this.importedUsers;
      const rows = this.ensureRowsHaveEmail(sourceRows, emailColumnIndex);
      const worksheetRows = this.excelHasHeader ? [headers, ...rows] : rows;
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
      const workbook = XLSX.utils.book_new();
      const fileName = this.getXlsxFileName(this.fileInfo.name || this.fileInfo.file?.name);
      const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      XLSX.utils.book_append_sheet(workbook, worksheet, this.$t("users.users"));
      const fileContent = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      return new File([fileContent], fileName, {
        type: mimeType,
      });
    },
    submitFile() {
      const self = this;
      self.is_loading = true;
      self.formErrors = [];

      if (
        this.$isSuperAdmin &&
        (self.company_id === null || self.company_id === "")
      ) {
        self.is_error = true;
        self.formErrors.push(this.$t("validation.one_item_required"));
        self.is_loading = false;
        return false;
      }

      if (!self.fileInfo.file) {
        self.is_error = true;
        self.formErrors.push(this.$t("validation.file_required"));
        self.is_loading = false;
        return false;
      }

      if (self.fileInfo.count === self.removedItems.length) {
        self.is_error = true;
        self.formErrors.push(this.$t("validation.one_item_required"));
        self.is_loading = false;
        return false;
      }

      const uploadFile = self.getUploadFile();
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("removed", self.removedItems);
      formData.append("user_group", self.user_group);
      formData.append("company_id", self.company_id);

      this.scrollToTop();
      store
        .dispatch(USER_MODULE + "/" + IMPORT_USERS, {
          url: USER_IMPORT_URL,
          contents: formData,
          returnType: "stateless",
          acceptPromise: true,
        })
        .then(() => {
          self.is_loading = false;
          if (!self.errors) {
            self.resetFileInfo();
            self.$router.push({
              name: "company.user.list",
              params: {
                succesMessage: this.$t("general.successfully_imported"),
              },
            });
          }
        }).catch((error) => {
          this.formErrors = [];

          const message = error?.response?.data?.message;
       
          if (message) {
            this.formErrors.push(message);
          } else {
            this.formErrors.push(this.$t("general.error_occurred"));
          }

          this.scrollToTop();
        })
        .finally(() => {
          self.is_loading = false;
        });
    },
    // Indirme web/native ayrimi utils/fileDownload.js icinde
    async exportSampleFile(event) {
      event.preventDefault();

      try {
        await downloadFromUrl({
          url: process.env.VUE_APP_BACKEND_APP_URL + this.sampleExcelEndpoint,
          headers: this.apiHeaders,
          fileName: exportFileName(this.sampleFileName),
        });
      } catch (error) {
        this.formErrors.push(await readBlobError(error));
      }
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
input[type="file"],
input[type="file"]::-webkit-file-upload-button {
  cursor: pointer;
}

.header-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  min-width: 90px;
}

.imported-users-table thead th {
  background-color: #f5f6fa;
  border-bottom: 2px solid #e4e6ef;
  vertical-align: bottom;
  font-weight: 600;
  color: #3f4254;
  padding: 10px 12px;
}

.imported-users-table tbody td {
  vertical-align: middle;
  padding: 10px 12px;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #eef0f5;
}

.imported-users-table tbody tr:nth-child(even) {
  background-color: #fafbfc;
}

.imported-users-table .col-index {
  width: 48px;
  text-align: center;
  color: #8c93a8;
  font-weight: 600;
}

.imported-users-table .col-action {
  width: 56px;
}

.file-upload-container {
  width: 100%;
  margin-bottom: 20px;
}

.file-upload-area {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: center;
  background-color: #f8f9fa;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  margin-bottom: 10px;
  overflow: hidden;
  min-height: 48px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.file-upload-area:active:not(.file-upload-disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.file-upload-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.file-upload-has-file {
  border-color: #28a745;
  background-color: #f0fff4;
}

.file-upload-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
  pointer-events: none;
}

.file-upload-content {
  position: relative;
  z-index: 1;
}

.file-upload-icon {
  font-size: 1.6rem;
  color: #5864ff;
  margin-bottom: 4px;
}

.file-upload-text {
  word-wrap: break-word;
  margin-bottom: 2px;
  font-size: 0.85rem;
}

.user-from-excel-uploader h5 {
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.user-from-excel-uploader h5.section-subtitle {
  font-size: 0.8rem;
  text-transform: none;
}

.user-from-excel-uploader .col-form-label {
  padding-top: 0;
  padding-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.file-info {
  color: #28a745;
  font-weight: 600;
}

.template-download-btn {
  width: 100%;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}

@media (max-width: 959px) {
  .user-from-excel-uploader .card {
    margin-top: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
  }

  .user-from-excel-uploader .uk-padding-large {
    padding: 16px !important;
  }

  .user-from-excel-uploader .uk-width-expand {
    border-left: 0 !important;
    border-top: 1px solid #e2e8f0;
    padding: 16px !important;
  }

  .user-from-excel-uploader h5,
  .user-from-excel-uploader h5.section-subtitle {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .user-from-excel-uploader .file-upload-text {
    font-size: 16px;
    line-height: 1.4;
  }

  .user-from-excel-uploader .uk-button,
  .user-from-excel-uploader .template-download-btn {
    width: 100%;
    min-height: 48px;
    margin: 0 !important;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
  }

  .user-from-excel-uploader .text-right {
    text-align: left !important;
  }

  .user-from-excel-uploader .uk-position-relative.my-4 {
    padding-right: 0 !important;
    max-height: none;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .imported-users-table tbody td,
  .imported-users-table thead th {
    font-size: 14px;
    padding: 12px 8px;
  }

  .user-from-excel-uploader .btn-circle {
    min-width: 48px;
    min-height: 48px;
  }

  .user-from-excel-uploader .table-responsive {
    margin: 0 -16px;
    padding: 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

@media (hover: hover) and (pointer: fine) {
  .file-upload-area:hover:not(.file-upload-disabled) {
    border-color: #ae71ff;
    background-color: #f5f0ff;
    cursor: pointer;
  }

  .template-download-btn:hover {
    cursor: pointer;
  }

  .imported-users-table tbody tr:hover {
    background-color: #f0f4ff;
  }
}

@media (prefers-reduced-motion: reduce) {
  .file-upload-area:active:not(.file-upload-disabled) {
    transform: none;
  }
}
</style>
